import { useQuery } from '@tanstack/react-query';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import {
  ResourceType,
  isChartArtifactPayload,
  isChartDashboardPayload,
  organizeLibraryTitle,
  organizeModeOf,
} from '@cotera/api';
import { resourceViewOf } from '@cotera/sdk/core';
import {
  artifactTypeAsResourceType,
  resourceIdFromArtifactPayload,
  type ArtifactDescriptor,
} from './artifacts';

type FolderArtifactPayload = { folderId?: string; name?: string };
type AgentArtifactPayload = { agentId?: string; agentKey?: string };

// agentKey can either be a human-readable slug (e.g. "daily-update-generator")
// or an auto-generated "agent-<hex>" identifier when the agent has no name yet.
// The latter is not useful to surface as a display title.
const AUTO_GENERATED_AGENT_KEY = /^agent-[a-f0-9]{16,}$/i;

const usableAgentKey = (agentKey: string | undefined): string | null => {
  if (agentKey === undefined || agentKey.length === 0) {
    return null;
  }
  if (AUTO_GENERATED_AGENT_KEY.test(agentKey)) {
    return null;
  }
  return agentKey;
};

export const getArtifactFallbackLabel = (
  /** `payload` is only read for types titled from it (organize-library). */
  artifact: Pick<ArtifactDescriptor, 'type' | 'artifactId'> & {
    payload?: unknown;
  },
  /**
   * Whether to append a slice of the id. On by default: the label is normally
   * reached because the artifact could *not* be resolved, and several
   * unresolvable artifacts in one chat would otherwise be indistinguishable.
   *
   * Pass `false` for an artifact that is new rather than unresolvable. There the
   * id disambiguates nothing — a draft the user just created is the only one on
   * screen — and "Untitled workflow (a1b2c3d4)" reads as though the name itself
   * had gone wrong.
   */
  options: { includeId?: boolean } = {}
): string => {
  const suffix =
    options.includeId === false ? '' : ` (${artifact.artifactId.slice(0, 8)})`;
  switch (artifact.type) {
    case 'document':
      return `Untitled document${suffix}`;
    case 'agent':
      return `Untitled agent${suffix}`;
    case 'folder':
      return `Untitled folder${suffix}`;
    case 'workflow':
      return `Untitled workflow${suffix}`;
    case 'dataset':
      return `Untitled dataset${suffix}`;
    case 'chart':
      return `Chart${suffix}`;
    case 'chart-dashboard':
      return `Dashboard${suffix}`;
    case 'workflow-node':
      return 'Workflow step';
    case 'organize-library':
      return organizeLibraryTitle(organizeModeOf(artifact.payload));
    default:
      return `${artifact.type}${suffix}`;
  }
};

type MinimalArtifact = {
  type: string;
  artifactId: string;
  payload?: unknown;
};

export const useArtifactTitle = (artifact: MinimalArtifact): string => {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();

  // Folders resolve from their payload below rather than being fetched, so they
  // are excluded here even though they are a workspace resource.
  const asResourceType = artifactTypeAsResourceType(artifact.type);
  const resourceType =
    asResourceType === null || asResourceType === ResourceType.Folder
      ? null
      : asResourceType;

  const resourceId =
    resourceType === null
      ? ''
      : resourceIdFromArtifactPayload(
          artifact.type,
          artifact.payload,
          artifact.artifactId
        ) ?? '';

  // Unpersisted seed artifacts (new agent/workflow) are not on the server yet.
  // Fetching them poisons the shared `resource.get` cache with NotFound, which
  // ChatArtifactAccessGate then surfaces as an "unshared" notice.
  //
  // Both flags mean the same thing by different routes: `isNew` rides the chat
  // seed when the action navigates, `isNewDraft` the payload when it opens a tab
  // in an existing panel.
  const payloadFlags =
    typeof artifact.payload === 'object' && artifact.payload !== null
      ? (artifact.payload as { isNew?: boolean; isNewDraft?: boolean })
      : null;
  const isNewArtifact =
    payloadFlags?.isNew === true || payloadFlags?.isNewDraft === true;

  const fallback = getArtifactFallbackLabel(
    {
      type: artifact.type,
      artifactId: artifact.artifactId,
      payload: artifact.payload,
    },
    { includeId: !isNewArtifact }
  );

  const enabled = resourceType !== null && resourceId !== '' && !isNewArtifact;

  const { data } = useQuery({
    queryKey:
      resourceType !== null
        ? queryKeys.resource.get({ orgId, type: resourceType, id: resourceId })
        : ['artifact-title-disabled', artifact.artifactId],
    queryFn: () => {
      if (resourceType === null) {
        return null;
      }
      return client.resource.get({ type: resourceType, id: resourceId });
    },
    enabled,
    staleTime: 60_000,
  });

  if (artifact.type === 'folder') {
    const payload = artifact.payload as FolderArtifactPayload | null;
    const name = payload?.name?.trim();
    return name !== undefined && name.length > 0 ? name : fallback;
  }

  if (artifact.type === 'chart' && isChartArtifactPayload(artifact.payload)) {
    const title = artifact.payload.title.trim();
    return title.length > 0 ? title : fallback;
  }

  if (
    artifact.type === 'chart-dashboard' &&
    isChartDashboardPayload(artifact.payload)
  ) {
    const title = artifact.payload.title?.trim() ?? '';
    return title.length > 0 ? title : fallback;
  }

  if (artifact.type === 'workflow-node') {
    const kind = (artifact.payload as { target?: { kind?: string } } | null)
      ?.target?.kind;
    return kind === 'trigger' ? 'Trigger' : 'Workflow step';
  }

  if (data === undefined || data === null || data.isErr() === true) {
    if (artifact.type === 'agent') {
      const agentKey = usableAgentKey(
        (artifact.payload as AgentArtifactPayload | null)?.agentKey
      );
      if (agentKey !== null) {
        return agentKey;
      }
    }
    return fallback;
  }

  // The artifact and the resource it stands for must agree, or this is a stale
  // fetch for a different artifact and its name would be the wrong one.
  const output = data.value;
  if (output.type !== resourceType) {
    return fallback;
  }
  return resourceViewOf(output).displayName() ?? fallback;
};
